import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import type {
  ChatRoomEntryResponse,
  ChatRoomResponse,
  ChatRoomType,
  ChatUnreadTotals,
  chatRoomMemberResponse,
  ExistingChatSummary,
  PaginatedResponse,
  User,
} from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

type ChatRoomSearchParams = {
  keyword: string;
  type?: ChatRoomType;
};

const normalizeSearchParams = (params: ChatRoomSearchParams) => {
  const formattedParams: {
    keyword: string;
    type?: ChatRoomType;
  } = {
    ...params,
  };
  return formattedParams;
};

const searchWithKeyword = async <T>(
  path: string,
  params: ChatRoomSearchParams,
): Promise<PaginatedResponse<T>> => {
  try {
    const formattedParams = normalizeSearchParams(params);
    const response = await api.get<PaginatedResponse<T>>(
      `${API_URLS.CHAT}/${path}`,
      {
        params: formattedParams,
      },
    );
    return response.data;
  } catch (error: unknown) {
    return handleApiError(error);
  }
};

const handleApiError = (error: unknown): never => {
  if (isAxiosError(error)) {
    if (!error.response) {
      throw new Error("Please check your connection");
    }
    console.error("API Error:", error.response.data);
    throw error;
  }
  throw new Error("Unexpected error occurred");
};

export const chatsApi = {
  getChatRooms: async (
    page = 1,
    perPage = 10,
    type?: ChatRoomType,
    includeHidden = false,
  ): Promise<PaginatedResponse<ChatRoomResponse>> => {
    try {
      const response = await api.get<PaginatedResponse<ChatRoomResponse>>(
        `${API_URLS.CHAT}`,
        {
          params: {
            page,
            limit: perPage,
            ...(type ? { type } : {}),
            ...(includeHidden ? { include_hidden: 1 } : {}),
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  getChatUnreadCounts: async (
    type?: ChatRoomType,
  ): Promise<ChatUnreadTotals> => {
    try {
      type ChatUnreadCountsResponse = {
        unread_message_count_total: number;
        unread_mention_count_total: number;
      };

      const response = await api.get<ChatUnreadCountsResponse>(
        `${API_URLS.CHAT}/unread-counts`,
        {
          params: {
            ...(type ? { type } : {}),
          },
        },
      );

      return {
        unreadMessages: response.data.unread_message_count_total ?? 0,
        unreadMentions: response.data.unread_mention_count_total ?? 0,
      };
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  findChatRoomByParticipants: async (
    participantIds: number[],
    type?: ChatRoomType,
  ): Promise<ExistingChatSummary[]> => {
    try {
      const response = await api.get<
        | ExistingChatSummary[]
        | { results?: ExistingChatSummary[]; messages?: ExistingChatSummary[] }
      >(`${API_URLS.CHAT}/find-by-participants`, {
        params: {
          participant_ids: participantIds.join(","),
          ...(type ? { type } : {}),
        },
      });
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return response.data.messages ?? response.data.results ?? [];
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  getChatRoomEntries: async (
    id: string | number,
    page = 1,
    perPage = 10,
  ): Promise<PaginatedResponse<ChatRoomEntryResponse>> => {
    try {
      const response = await api.get<PaginatedResponse<ChatRoomEntryResponse>>(
        `${API_URLS.CHAT}/${id}/entries-with-user-short`,
        {
          params: {
            page,
            limit: perPage,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  getChatRoom: async (id: string | number): Promise<ChatRoomResponse> => {
    try {
      const response = await api.get<ChatRoomResponse>(
        `${API_URLS.CHAT}/${id}`,
      );
      return response.data;
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  getChatRoomMembers: async (
    id: string | number,
  ): Promise<chatRoomMemberResponse> => {
    try {
      const response = await api.get<chatRoomMemberResponse>(
        `${API_URLS.CHAT}/${id}/users`,
      );
      return response.data;
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  createChatRoomEntry: async (
    id: string | number,
    data: FormData | string,
  ): Promise<ChatRoomEntryResponse> => {
    try {
      let response: { data: ChatRoomEntryResponse };

      if (data instanceof FormData) {
        // FormDataの場合はmultipart/form-dataとして送信
        // APIの要求に合わせてキー名を設定
        response = await api.post(`${API_URLS.CHAT}/${id}/entry`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // 文字列の場合はFormDataに変換して送信
        const formData = new FormData();
        formData.append("ReplyForm[message]", data);

        response = await api.post(`${API_URLS.CHAT}/${id}/entry`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      return response.data;
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  createChatRoom: async (
    title: string,
    message: string,
    recipients: string[],
  ): Promise<ChatRoomResponse> => {
    try {
      const trimmedTitle = title.trim();
      const type = trimmedTitle.length === 0 ? "user_dm" : "user_group";
      const response = await api.post<ChatRoomResponse>(API_URLS.CHAT, {
        title: trimmedTitle,
        message,
        recipient: recipients,
        type,
      });
      return response.data;
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  getMentionAvailableUsers: async (params?: {
    keyword?: string;
    messageId?: string | number;
  }): Promise<User[]> => {
    try {
      interface MentionUserResponse {
        results?: Array<{ user: User }>;
      }

      const response = await api.get<MentionUserResponse>(
        `${API_URLS.CHAT}/filter-mention-available-users`,
        {
          params,
        },
      );

      // APIレスポンスから直接User[]を抽出
      return response.data.results?.map((item) => item.user) || [];
    } catch (error: unknown) {
      return handleApiError(error);
    }
  },
  addChatRoomMember: async (
    chatRoomId: string | number,
    userIds: string[] | number[],
  ): Promise<void> => {
    try {
      await api.post(`${API_URLS.CHAT}/${chatRoomId}/user/add-multiple`, {
        userIds,
      });
    } catch (error: unknown) {
      handleApiError(error);
    }
  },
  deleteChatRoomEntry: async (
    chatRoomId: string | number,
    entryId: string | number,
  ): Promise<void> => {
    try {
      await api.delete(`${API_URLS.CHAT}/${chatRoomId}/entry/${entryId}`);
    } catch (error: unknown) {
      handleApiError(error);
    }
  },
  leaveChatRoom: async (chatRoomId: string | number): Promise<void> => {
    try {
      await api.delete(
        `${API_URLS.CHAT}/${chatRoomId}/user/leave-current-user`,
      );
    } catch (error: unknown) {
      handleApiError(error);
    }
  },
  updateChatRoomTitle: async (
    chatRoomId: string | number,
    title: string,
  ): Promise<void> => {
    try {
      await api.put(`${API_URLS.CHAT}/${chatRoomId}/title`, {
        title,
      });
    } catch (error: unknown) {
      handleApiError(error);
    }
  },
  hideChatRoom: async (
    messageId: string | number,
    hidden: boolean | number = true,
  ): Promise<void> => {
    try {
      await api.put(
        `${API_URLS.CHAT}/${messageId}/hidden`,
        { hidden },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } catch (error: unknown) {
      handleApiError(error);
    }
  },
  visitChatRoom: async (chatRoomId: string | number): Promise<void> => {
    try {
      await api.put(`${API_URLS.CHAT}/${chatRoomId}`);
    } catch (error: unknown) {
      handleApiError(error);
    }
  },
  searchEntries: async (
    params: ChatRoomSearchParams,
  ): Promise<PaginatedResponse<ChatRoomEntryResponse>> => {
    return searchWithKeyword<ChatRoomEntryResponse>(
      "entry/search-with-keyword",
      params,
    );
  },
  searchChatRooms: async (
    params: ChatRoomSearchParams,
  ): Promise<PaginatedResponse<ChatRoomResponse>> => {
    return searchWithKeyword<ChatRoomResponse>("search-with-keyword", params);
  },
};
